'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCustomAddressStore } from '@libs/common-next';
import type { Product, TailoredApiData } from '@libs/common-types';
import { type DeliveryInventoryStatus, getBopisFulfillmentStatus, getDeliveryInventoryStatus, getSddFulfillmentStatus, getSthFulfillmentStatus, resolveFulfillmentMethod, type SddDetailsProps } from '../../product-details';
import { isMethodAvailableForPurchaseOption } from '../lib';
import type { MiniPdpForm } from '../types';
export type MiniPdpFulfillmentProps = Readonly<{
  product: Product;
  tailored: TailoredApiData | null;
  isLoading: boolean;
  isError?: boolean;
  onOpenSddAddress?: () => void;
  onOpenStoreDrawer: () => void;
}>;
export function useMiniPdpFulfillment(t0) {
  const $ = _c(85);
  const {
    product,
    tailored,
    isLoading,
    isError: t1,
    onOpenSddAddress
  } = t0;
  const isError = t1 === undefined ? false : t1;
  const {
    register,
    setValue
  } = useFormContext();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "fulfilmentMethod"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const selectedMethod = useWatch(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      name: "selectedSku"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const selectedSku = useWatch(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      name: "purchaseOption"
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const purchaseOption = useWatch(t4);
  const customAddress = useCustomAddressStore(_temp);
  const tailoredVariant = selectedSku ? tailored?.variants[selectedSku] : undefined;
  const bopis = tailoredVariant?.BOPIS;
  const sdd = tailoredVariant?.SDD;
  const sth = tailoredVariant?.STH;
  let t5;
  if ($[3] !== product || $[4] !== purchaseOption) {
    t5 = isMethodAvailableForPurchaseOption(product, "BOPIS", purchaseOption);
    $[3] = product;
    $[4] = purchaseOption;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const isBopisAvailableForPurchase = t5;
  const isBopisAvailableForStore = bopis?.storeId ? tailored?.bopisStoreData?.[bopis.storeId]?.offersBopis ?? true : true;
  const isBopisAvailable = isBopisAvailableForPurchase && isBopisAvailableForStore;
  let t6;
  if ($[6] !== product || $[7] !== purchaseOption) {
    t6 = isMethodAvailableForPurchaseOption(product, "SDD", purchaseOption);
    $[6] = product;
    $[7] = purchaseOption;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const isSddAvailable = t6;
  let t7;
  if ($[9] !== product || $[10] !== purchaseOption) {
    t7 = isMethodAvailableForPurchaseOption(product, "STH", purchaseOption);
    $[9] = product;
    $[10] = purchaseOption;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const isSthAvailable = t7;
  let t8;
  if ($[12] !== isBopisAvailable || $[13] !== isSddAvailable || $[14] !== isSthAvailable) {
    t8 = {
      bopis: isBopisAvailable,
      sdd: isSddAvailable,
      sth: isSthAvailable
    };
    $[12] = isBopisAvailable;
    $[13] = isSddAvailable;
    $[14] = isSthAvailable;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const availableMethods = t8;
  let t9;
  if ($[16] !== availableMethods || $[17] !== isLoading || $[18] !== selectedMethod || $[19] !== setValue) {
    t9 = () => {
      if (isLoading) {
        return;
      }
      const resolvedMethod = resolveFulfillmentMethod({
        availableMethods,
        selectedMethod
      });
      if (resolvedMethod !== selectedMethod) {
        setValue("fulfilmentMethod", resolvedMethod);
      }
    };
    $[16] = availableMethods;
    $[17] = isLoading;
    $[18] = selectedMethod;
    $[19] = setValue;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== isBopisAvailable || $[22] !== isLoading || $[23] !== isSddAvailable || $[24] !== isSthAvailable || $[25] !== selectedMethod || $[26] !== setValue) {
    t10 = [isLoading, selectedMethod, setValue, isBopisAvailable, isSddAvailable, isSthAvailable];
    $[21] = isBopisAvailable;
    $[22] = isLoading;
    $[23] = isSddAvailable;
    $[24] = isSthAvailable;
    $[25] = selectedMethod;
    $[26] = setValue;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  useEffect(t9, t10);
  const t11 = bopis?.availableStock ?? 0;
  const t12 = bopis?.altAvailableStock ?? 0;
  const t13 = product.deliveryMethods.BOPIS.purchaseQuantities?.min ?? 1;
  const t14 = Boolean(bopis?.storeId);
  const t15 = Boolean(product.pharmacy);
  let t16;
  if ($[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== t15) {
    t16 = getBopisFulfillmentStatus({
      availableStock: t11,
      altAvailableStock: t12,
      minimumCaseQty: t13,
      isStoreSelected: t14,
      isPharmacy: t15
    });
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = selectedMethod === "bopis" && !isLoading && isBopisAvailable;
  const t18 = !isLoading && !isBopisAvailable;
  let t19;
  if ($[34] !== isLoading || $[35] !== t16 || $[36] !== t17 || $[37] !== t18) {
    t19 = {
      value: "bopis" as const,
      title: "Pick up in store",
      iconName: "store-outline" as const,
      statusTestId: "bopis-tile-status-message",
      status: t16,
      isSelected: t17,
      isDisabled: t18,
      isLoading
    };
    $[34] = isLoading;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const t20 = sdd?.availableStock ?? 0;
  const t21 = product.deliveryMethods.SDD.purchaseQuantities?.min ?? 1;
  const t22 = Boolean(sdd?.doorDashValidationSuccess);
  const t23 = Boolean(customAddress?.streetLine1);
  let t24;
  if ($[39] !== t20 || $[40] !== t21 || $[41] !== t22 || $[42] !== t23) {
    t24 = getSddFulfillmentStatus({
      availableStock: t20,
      minimumCaseQty: t21,
      isAvailable: t22,
      hasStreetAddress: t23
    });
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  const t25 = selectedMethod === "sdd" && !isLoading && isSddAvailable;
  const t26 = !isLoading && !isSddAvailable;
  let t27;
  if ($[44] !== isLoading || $[45] !== t24 || $[46] !== t25 || $[47] !== t26) {
    t27 = {
      value: "sdd" as const,
      title: "Same-day delivery",
      iconName: "box-outline" as const,
      statusTestId: "sdd-tile-status-message",
      status: t24,
      isSelected: t25,
      isDisabled: t26,
      isLoading
    };
    $[44] = isLoading;
    $[45] = t24;
    $[46] = t25;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  const t28 = sth?.availableStock ?? 0;
  const t29 = product.deliveryMethods.STH.purchaseQuantities?.min ?? 1;
  const t30 = Boolean(product.pharmacy?.oos);
  let t31;
  if ($[49] !== t28 || $[50] !== t29 || $[51] !== t30) {
    t31 = getSthFulfillmentStatus({
      availableStock: t28,
      minimumCaseQty: t29,
      isPharmacyOutOfStock: t30
    });
    $[49] = t28;
    $[50] = t29;
    $[51] = t30;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  const t32 = selectedMethod === "sth" && !isLoading && isSthAvailable;
  const t33 = !isLoading && !isSthAvailable;
  let t34;
  if ($[53] !== isLoading || $[54] !== t31 || $[55] !== t32 || $[56] !== t33) {
    t34 = {
      value: "sth" as const,
      title: "Ship to me",
      iconName: "car-outline" as const,
      statusTestId: "stm-tile-status-message",
      status: t31,
      isSelected: t32,
      isDisabled: t33,
      isLoading
    };
    $[53] = isLoading;
    $[54] = t31;
    $[55] = t32;
    $[56] = t33;
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  let t35;
  if ($[58] !== t19 || $[59] !== t27 || $[60] !== t34) {
    t35 = [t19, t27, t34];
    $[58] = t19;
    $[59] = t27;
    $[60] = t34;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  const fulfillmentMethods = t35;
  let t36;
  if ($[62] !== customAddress?.streetLine1 || $[63] !== isError || $[64] !== isLoading || $[65] !== onOpenSddAddress || $[66] !== product.deliveryMethods.SDD.purchaseQuantities?.min || $[67] !== sdd?.availableStock || $[68] !== sdd?.doorDashValidationSuccess || $[69] !== selectedMethod) {
    t36 = selectedMethod === "sdd" && !isError ? {
      isLoading,
      streetAddress: customAddress?.streetLine1,
      availableStock: sdd?.availableStock ?? 0,
      minimumCaseQty: product.deliveryMethods.SDD.purchaseQuantities?.min ?? 1,
      isDoorDashAvailable: Boolean(sdd?.doorDashValidationSuccess),
      onOpenAddressDrawer: onOpenSddAddress
    } : null;
    $[62] = customAddress?.streetLine1;
    $[63] = isError;
    $[64] = isLoading;
    $[65] = onOpenSddAddress;
    $[66] = product.deliveryMethods.SDD.purchaseQuantities?.min;
    $[67] = sdd?.availableStock;
    $[68] = sdd?.doorDashValidationSuccess;
    $[69] = selectedMethod;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  const sddDetails = t36;
  let t37;
  if ($[71] !== isError || $[72] !== isLoading || $[73] !== product.deliveryMethods.STH.purchaseQuantities?.min || $[74] !== product.pharmacy?.oos || $[75] !== selectedMethod || $[76] !== sth?.availableStock) {
    t37 = selectedMethod === "sth" && !isError ? getDeliveryInventoryStatus({
      method: "sth",
      isLoading,
      availableStock: sth?.availableStock,
      minimumCaseQty: product.deliveryMethods.STH.purchaseQuantities?.min ?? 1,
      customPharmacyOOS: Boolean(product.pharmacy?.oos)
    }) : null;
    $[71] = isError;
    $[72] = isLoading;
    $[73] = product.deliveryMethods.STH.purchaseQuantities?.min;
    $[74] = product.pharmacy?.oos;
    $[75] = selectedMethod;
    $[76] = sth?.availableStock;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  const inventoryStatus = t37;
  let t38;
  if ($[78] !== register) {
    t38 = register("fulfilmentMethod");
    $[78] = register;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== fulfillmentMethods || $[81] !== inventoryStatus || $[82] !== sddDetails || $[83] !== t38) {
    t39 = {
      fulfillmentMethods,
      register: t38,
      sddDetails,
      inventoryStatus
    };
    $[80] = fulfillmentMethods;
    $[81] = inventoryStatus;
    $[82] = sddDetails;
    $[83] = t38;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  return t39;
}
function _temp(state) {
  return state.address;
}